import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { getWorkbookId, getCachedData } from '../../services/plmsService';
import { OnboardingItem } from '../../types/plms';
import { ExternalLink } from 'lucide-react';

export const MasterLists: React.FC = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<OnboardingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadMasterLists = useCallback(async () => {
    if (!user?.email) return;
    
    try {
      const workbookId = await getWorkbookId(user.email);
      if (!workbookId) {
        setError('No workbook found');
        return;
      }

      const { items: masterLists } = await getCachedData(user.email, workbookId, {
        includeMasterLists: true,
        day: 0
      });
      setItems(masterLists);
    } catch (error) {
      console.error('Error loading master lists:', error);
      setError('Failed to load master lists');
    } finally {
      setLoading(false);
    }
  }, [user?.email, setError, setItems, setLoading]);

  useEffect(() => {
    loadMasterLists();
  }, [loadMasterLists]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>Master Lists</Typography>
      
      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Section</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '60%' }}>Item</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '10%' }} align="center">Link</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow 
                key={index}
                sx={{ '&:hover': { bgcolor: 'action.hover' } }}
              >
                <TableCell>{item.section}</TableCell>
                <TableCell>{item.item}</TableCell>
                <TableCell align="center">
                  {item.url && (
                    <Tooltip title="Open Link">
                      <IconButton
                        size="small"
                        onClick={() => window.open(item.url, '_blank')}
                        sx={{ color: 'primary.main' }}
                      >
                        <ExternalLink size={18} />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
